/**
 * ЄДИНЕ місце прив'язки «ключ поля інтерфейсу → поняття довідника».
 *
 * Ключ поля — стабільний ідентифікатор поля на екрані: `<модуль>.<сутність>.<поле>`
 * (напр. `content.material.commercialValue`), а для користувацьких полів задачі —
 * `task.customField:<назва поля>` (див. customFieldKey).
 *
 * Порядок пошуку поняття для ключа (termForField):
 *   1) ця мапа FIELD_TERMS;
 *   2) поле `fieldKeys` понять із сервера (модуль бази знань додасть його згодом);
 *   3) для `task.customField:<назва>` — сама назва поля як термін.
 * Якщо поняття з таким терміном на сервері немає, значок «?» просто не показується.
 */

import { AI_FIELD_TERMS } from '../ai/fieldTerms';
import { AUTOMATION_FIELD_TERMS } from '../automation/fieldTerms';
import { CONTENT_FIELD_TERMS } from '../content/fieldTerms';
import { FINANCE_FIELD_TERMS } from '../finance/fieldTerms';
import { KB_FIELD_TERMS } from '../kb/fieldTerms';
import { LEGAL_FIELD_TERMS } from '../legal/fieldTerms';
import { MGMT_FIELD_TERMS } from '../mgmt/fieldTerms';
import { CONNECTORS_FIELD_TERMS } from '../connectors/fieldTerms';
import { MEETINGS_FIELD_TERMS } from '../meetings/fieldTerms';
import { MIGRATION_FIELD_TERMS } from '../migration/fieldTerms';
import { PEOPLE_FIELD_TERMS } from '../people/fieldTerms';
import { REPORTS_FIELD_TERMS } from '../reports/fieldTerms';
import { TIME_FIELD_TERMS } from '../time/fieldTerms';

/** Префікс ключа користувацького поля задачі. */
export const CUSTOM_FIELD_PREFIX = 'task.customField:';

/**
 * Стартові відповідності. Терміни — як у словнику сервера (GET /api/v1/kb/glossary):
 * «ЦА» й «КЦ» є в офіційному «Списку скорочень»; «Портрет клієнта», «Якість», «Потреба»
 * — припущення: значок з'явиться, щойно ці поняття додадуть до словника.
 */
export const FIELD_TERMS: Readonly<Record<string, string>> = {
  // Картка замовлення: бібліотека полів задачі.
  'task.customField:Портрет клієнта': 'Портрет клієнта',

  // Юридичний контур: клієнт і справа.
  'legal.client.portrait': 'Портрет клієнта',
  'legal.matter.portrait': 'Портрет клієнта',
  'legal.oe': 'ОЕ',

  // Контент: матеріали й паспорти послуг.
  'content.material.commercialValue': 'КЦ',
  'content.material.targetAudience': 'ЦА',
  'content.passport.commercialValue': 'КЦ',
  'content.passport.targetAudience': 'ЦА',
  'content.audience': 'ЦА',

  // Загальні ключі, коли поле трапляється в кількох модулях.
  commercialValue: 'КЦ',
  targetAudience: 'ЦА',
  quality: 'Якість',
  need: 'Потреба',

  // Модулі хвилі 2Б: процеси й навчання, контент, ШІ, автоматизація (мапи в папках модулів).
  ...KB_FIELD_TERMS,
  ...CONTENT_FIELD_TERMS,
  ...AI_FIELD_TERMS,
  ...AUTOMATION_FIELD_TERMS,

  // Модулі хвилі 2А: облік часу, юридичний і управлінський контури (мапи в папках модулів).
  ...TIME_FIELD_TERMS,
  ...LEGAL_FIELD_TERMS,
  ...MGMT_FIELD_TERMS,

  // Модулі хвилі 2В: персонал, інтеграції, перенесення даних, зустрічі (мапи в папках модулів).
  ...PEOPLE_FIELD_TERMS,
  ...CONNECTORS_FIELD_TERMS,
  ...MIGRATION_FIELD_TERMS,
  ...MEETINGS_FIELD_TERMS,

  // Модулі хвилі 2Г: фінанси робіт, звіти й аналітика (мапи в папках модулів).
  ...FINANCE_FIELD_TERMS,
  ...REPORTS_FIELD_TERMS,
};

/** Ключ користувацького поля задачі за його назвою: customFieldKey('Портрет клієнта'). */
export function customFieldKey(fieldName: string): string {
  return `${CUSTOM_FIELD_PREFIX}${fieldName.trim()}`;
}

/** Термін із мапи FIELD_TERMS (без запасних варіантів). */
export function mappedTerm(fieldKey: string): string | null {
  const key = fieldKey.trim();
  return Object.prototype.hasOwnProperty.call(FIELD_TERMS, key) ? (FIELD_TERMS[key] ?? null) : null;
}

/**
 * Термін для ключа поля: мапа → `fieldKeys` понять із сервера (якщо передано) → назва користувацького поля.
 * Повертає null, якщо поняття для поля не визначено.
 */
export function termForField(fieldKey: string, serverFieldTerms?: ReadonlyMap<string, string> | null): string | null {
  const key = fieldKey.trim();
  if (!key) return null;
  const mapped = mappedTerm(key);
  if (mapped) return mapped;
  const fromServer = serverFieldTerms?.get(key);
  if (fromServer) return fromServer;
  if (key.startsWith(CUSTOM_FIELD_PREFIX)) {
    const name = key.slice(CUSTOM_FIELD_PREFIX.length).trim();
    return name || null;
  }
  return null;
}

/**
 * Чи рядок — ключ поля (а не сам термін). Ключі пишуться латиницею з малої літери
 * (`quality`, `content.material.commercialValue`) або мають префікс користувацького поля.
 * Терміни словника — кирилиця чи великі латинські абревіатури (КЦ, CRM), тож не збігаються.
 */
export function isFieldKey(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  if (mappedTerm(v) || v.startsWith(CUSTOM_FIELD_PREFIX)) return true;
  return /^[a-z][A-Za-z0-9_.:-]*$/.test(v);
}
